import { trackError } from '@iheartjane/tracking';
import type { User } from '@iheartjane/types';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import snakeCase from 'lodash-es/snakeCase';
import type { NavigateFunction } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import type { SSOChannel } from '@jane/shared-ecomm/tracking';
import {
  trackCustomerLogin,
  trackCustomerRegistration,
} from '@jane/shared-ecomm/tracking';
import { removeJwt, setJwt } from '@jane/shared/auth';
import type { AppleAuthResponse } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { LoginCredentials } from '@jane/shared/models';
import {
  isEmbeddedMode,
  isStore,
  monolithPaths as paths,
} from '@jane/shared/util';

import { HANDLE_INVALID_LOGIN_TOKEN } from '../../customer/redux/login';
import { CustomerSource } from '../../sources/customer';
import { UsersSource } from '../../sources/users';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { getCartForUser, removeCartUser } from './cart';
import { VERIFY_STORE_SUCCESS } from './embeddedApp';
import { UPDATE_IDENTIFICATION_SUCCESS } from './identification';
import { UPDATE_USERS } from './users';

export const CUSTOMER_LOG_OUT = 'customer/log-out';
const customerLogOutSuccess = createAction<void, typeof CUSTOMER_LOG_OUT>(
  CUSTOMER_LOG_OUT
);

export const logOut =
  (onSuccess?: () => void): CustomerThunkAction =>
  (dispatch) => {
    CustomerSource.logOut()
      .then(() => removeJwt())
      .then(() => {
        dispatch(customerLogOutSuccess());
        dispatch(removeCartUser());
        onSuccess && onSuccess();
      });
  };

const CUSTOMER_SET_SIGNED_IN = 'customer/set-signed-in';
const setSignedInUserSuccess = createAction<
  User,
  typeof CUSTOMER_SET_SIGNED_IN
>(CUSTOMER_SET_SIGNED_IN);

export const setSignedInUser =
  ({ user, jwt }: { jwt: string; user: User }): CustomerThunkAction =>
  (dispatch) => {
    if (!jwt) {
      return;
    }
    setJwt(`Bearer ${jwt}`);
    dispatch(setSignedInUserSuccess(user));
  };

export const CUSTOMER_LOG_IN = 'customer/log-in';
const initiateCustomerLogIn = createAction<void, typeof CUSTOMER_LOG_IN>(
  CUSTOMER_LOG_IN
);

export const logIn =
  ({
    credentials,
    nextPathname,
    navigate,
    source,
  }: {
    credentials: LoginCredentials;
    navigate: NavigateFunction;
    nextPathname: string | null;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateCustomerLogIn());

    CustomerSource.logIn(credentials).then((response) => {
      const { body, headers } = response;
      const { user, errors } = body;

      if (errors) {
        return dispatch(logInError(errors.error));
      }

      return dispatch(
        logInSuccess({ user, headers, nextPathname, navigate, source })
      );
    });
  };

export const logInWithCognitoToken =
  (token: string): CustomerThunkAction<Promise<unknown>> =>
  (dispatch) => {
    dispatch(initiateCustomerLogIn());

    return CustomerSource.logInWithCognitoToken(token).then((response) => {
      const { body, headers } = response;
      const { user, error } = body;

      if (body.status === 404) {
        return dispatch(logInError(error));
      }

      // handle expired tokens
      if (!body.status && body.errors) {
        return dispatch(logInError(body.errors.error));
      }

      return dispatch(headlessLogInSuccess({ user, headers }));
    });
  };

export const SET_LOCAL_USER = 'customer/set-local';
export const setLocalUser = createAction<
  Partial<User> & object,
  typeof SET_LOCAL_USER
>(SET_LOCAL_USER);

export const CUSTOMER_LOG_IN_SUCCESS = 'customer/log-in-success';
const logInSucceeded = createAction<User, typeof CUSTOMER_LOG_IN_SUCCESS>(
  CUSTOMER_LOG_IN_SUCCESS
);

export const logInSuccess =
  ({
    user,
    headers,
    nextPathname,
    source,
    ssoChannel,
    navigate,
  }: {
    headers: Record<string, string>;
    navigate?: NavigateFunction | false;
    nextPathname: string | null;
    source?: string;
    ssoChannel?: SSOChannel;
    user: User;
  }): CustomerThunkAction =>
  (dispatch, _, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const isEmbed = isEmbeddedMode(appMode);
    if (headers && headers['Authorization']) {
      setJwt(headers['Authorization']);
    }
    trackCustomerLogin({ ssoChannel, source });
    dispatch(logInSucceeded(user));

    dispatch(getCartForUser());

    const path =
      nextPathname && !nextPathname.includes('logout')
        ? nextPathname
        : isEmbed
          ? paths.embeddedMenu()
          : paths.root();

    if (navigate) {
      navigate(path, { replace: true });
    }
  };

const HEADLESS_CUSTOMER_LOG_IN_SUCCESS = 'customer/headless-log-in-success';
const headlessLogInSucceeded = createAction<
  User,
  typeof HEADLESS_CUSTOMER_LOG_IN_SUCCESS
>(HEADLESS_CUSTOMER_LOG_IN_SUCCESS);

const headlessLogInSuccess =
  ({
    headers,
    user,
  }: {
    headers: Record<string, string>;
    user: User;
  }): CustomerThunkAction =>
  (dispatch) => {
    if (headers && headers['Authorization']) {
      setJwt(headers['Authorization'], true);
    }
    trackCustomerLogin({ ssoChannel: 'cognito', source: 'headless' });
    dispatch(headlessLogInSucceeded(user));
  };

const CUSTOMER_LOG_IN_ERROR = 'customer/log-in-error';
export const logInError = createAction<
  string | null,
  typeof CUSTOMER_LOG_IN_ERROR
>(CUSTOMER_LOG_IN_ERROR);

export const CUSTOMER_REGISTER_SUCCESS = 'customer/register-success';
const customerRegisterSuccess = createAction<
  User,
  typeof CUSTOMER_REGISTER_SUCCESS
>(CUSTOMER_REGISTER_SUCCESS);

export const registerSuccess =
  (user: User): CustomerThunkAction =>
  (dispatch) => {
    dispatch(getCartForUser());
    dispatch(customerRegisterSuccess(user));
  };

const CUSTOMER_REGISTER_ERROR = 'customer/register-error';
export const registerError = createAction<
  string | null,
  typeof CUSTOMER_REGISTER_ERROR
>(CUSTOMER_REGISTER_ERROR);

export const CUSTOMER_WHOAMI = 'customer/whoami';
const initiateWhoami = createAction<void, typeof CUSTOMER_WHOAMI>(
  CUSTOMER_WHOAMI
);

export const whoami =
  ({ ref }: { ref?: string } = {}): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateWhoami());

    CustomerSource.whoami({ ref }).then(
      (response) => {
        const { user } = response;
        return dispatch(whoamiSuccess(user));
      },
      (err) => dispatch(whoamiError(err))
    );
  };

export const CUSTOMER_WHOAMI_ERROR = 'customer/whoami-error';
export const whoamiError = createAction<unknown, typeof CUSTOMER_WHOAMI_ERROR>(
  CUSTOMER_WHOAMI_ERROR
);

export const CUSTOMER_WHOAMI_SUCCESS = 'customer/whoami-success';
export const whoamiSuccess = createAction<User, typeof CUSTOMER_WHOAMI_SUCCESS>(
  CUSTOMER_WHOAMI_SUCCESS
);

export const CUSTOMER_AUTHENTICATE_GOOGLE_CODE =
  'customer/authenticate-google-code';
const initiateAuthenticateGoogleCode = createAction<
  void,
  typeof CUSTOMER_AUTHENTICATE_GOOGLE_CODE
>(CUSTOMER_AUTHENTICATE_GOOGLE_CODE);

export const authenticateGoogleCode =
  ({
    code,
    nextPathname = null,
    onAuthSuccess,
    navigate,
    source,
  }: {
    code: any;
    navigate: NavigateFunction | false;
    nextPathname: string | null;
    onAuthSuccess?: () => void;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    dispatch(initiateAuthenticateGoogleCode());

    const state = getState();
    const { partnerId, partnerName } = state.embeddedApp;
    const { store } = state.store;
    const storeId = isStore(store) ? store.id : undefined;

    CustomerSource.authenticateGoogleCode(code)
      .then((response) => {
        const { body, headers } = response;
        // track new oauth identity linked to existing account:
        const { user, errors } = body;

        if (!user || errors) {
          return dispatch(logInError(errors.error));
        }

        user.authenticated = true;

        dispatch(
          logInSuccess({
            user,
            headers,
            nextPathname,
            source,
            ssoChannel: 'google',
            navigate,
          })
        );

        if (user && user.new_registration) {
          UsersSource.update({
            user: {
              app_mode_at_registration: snakeCase(appMode),
              store_id_at_registration: storeId,
            },
          }).catch((error) => {
            trackError(new Error('User update failed'), {
              error,
            });
          });

          trackCustomerRegistration({
            customer: { ...user, ssoChannel: 'google' },
            partnerChannel: appMode,
            partnerId,
            partnerName,
          });
        }

        onAuthSuccess && onAuthSuccess();
        return undefined;
      })
      .catch((error) =>
        trackError(new Error('Google authentication failed'), {
          error,
        })
      );
  };

interface AppleAuthParams {
  first_name?: string;
  id_token: string;
  last_name?: string;
}

const CUSTOMER_AUTHENTICATE_APPLE_CODE = 'customer/authenticate-apple-code';
const initiateAuthenticateAppleCode = createAction<
  void,
  typeof CUSTOMER_AUTHENTICATE_APPLE_CODE
>(CUSTOMER_AUTHENTICATE_APPLE_CODE);

export const authenticateAppleCode =
  ({
    authMetadata,
    nextPathname = null,
    onAuthSuccess,
    navigate,
    source,
  }: {
    authMetadata: AppleAuthResponse;
    navigate: NavigateFunction | false;
    nextPathname: string | null;
    onAuthSuccess?: () => void;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const auth: AppleAuthParams = {
      id_token: authMetadata.authorization.id_token,
    };

    if (authMetadata.user) {
      auth.first_name = authMetadata.user?.name?.firstName;
      auth.last_name = authMetadata.user?.name?.lastName;
    }

    dispatch(initiateAuthenticateAppleCode());

    const state = getState();
    const { partnerId, partnerName } = state.embeddedApp;
    const { store } = state.store;
    const storeId = isStore(store) ? store.id : undefined;

    CustomerSource.authenticateAppleCode(auth)
      .then((response) => {
        const { body, headers } = response;
        // track new oauth identity linked to existing account:
        const { user, errors } = body;

        if (!user || errors) {
          return dispatch(logInError(errors.error));
        }

        user.authenticated = true;

        dispatch(
          logInSuccess({
            user,
            headers,
            nextPathname,
            source,
            ssoChannel: 'apple',
            navigate,
          })
        );

        if (user && user.new_registration) {
          UsersSource.update({
            user: {
              app_mode_at_registration: snakeCase(appMode),
              store_id_at_registration: storeId,
            },
          }).catch((error) => {
            trackError(new Error('User update failed'), {
              error,
            });
          });

          trackCustomerRegistration({
            customer: { ...user, ssoChannel: 'apple' },
            partnerChannel: appMode,
            partnerId,
            partnerName,
          });
        }

        onAuthSuccess && onAuthSuccess();
        return undefined;
      })
      .catch((error) =>
        trackError(new Error('Apple authentication failed'), {
          error,
        })
      );
  };

export type CustomerActions =
  | ReturnType<typeof customerLogOutSuccess>
  | ReturnType<typeof initiateCustomerLogIn>
  | ReturnType<typeof logInError>
  | ReturnType<typeof customerRegisterSuccess>
  | ReturnType<typeof registerError>
  | ReturnType<typeof initiateWhoami>
  | ReturnType<typeof whoamiSuccess>
  | ReturnType<typeof whoamiError>
  | ReturnType<typeof initiateAuthenticateGoogleCode>
  | ReturnType<typeof logInSucceeded>
  | ReturnType<typeof setSignedInUserSuccess>
  | ReturnType<typeof headlessLogInSucceeded>
  | ReturnType<typeof setLocalUser>
  | ReturnType<typeof initiateAuthenticateAppleCode>;

export type CustomerReducerState = DeepReadonly<{
  authenticated: boolean;
  avatar: number | null;
  birth_date: string;
  email: string | null;
  externalId: string | null;
  firstName: string | null;
  id: number | null;
  isEmbeddedMode: boolean;
  lastMfa: Date | null;
  lastName: string | null;
  loading: boolean;
  logInErrorMessage: string | null;
  name: string | null;
  nickname: string | null;
  phone: string | null;
  promotionsOptOut: boolean;
  sessionChecked: boolean;
}>;

const getInitialState = (): CustomerReducerState => ({
  isEmbeddedMode: false,
  sessionChecked: false,
  loading: false,
  logInErrorMessage: null,
  authenticated: false,
  avatar: null,
  birth_date: '',
  email: null,
  externalId: null,
  firstName: null,
  id: null,
  lastName: null,
  name: null,
  nickname: null,
  phone: null,
  promotionsOptOut: false,
  lastMfa: null,
});

const setUser = (state: CustomerReducerState, user: User) => ({
  ...state,
  authenticated: user.authenticated || false,
  avatar: user.avatar,
  birth_date: user['birth_date'] || '',
  email: user.email || '',
  externalId: user.externalId || null,
  firstName: user['first_name'] || '',
  id: user.id,
  lastName: user['last_name'],
  name: user.name,
  nickname: user.nickname,
  phone: user.phone,
  promotionsOptOut: user['promotions_opt_out'],
});

const setUserFromPartialIdentification = (
  state: CustomerReducerState,
  user: Partial<User>
) => {
  const firstName =
    typeof user.first_name === 'string' ? user.first_name : state.firstName;
  const lastName =
    typeof user.last_name === 'string' ? user.last_name : state.lastName;
  const name = [firstName, lastName].join(' ');

  return {
    ...state,
    firstName,
    lastName,
    name,
  };
};

const reset = (state: CustomerReducerState) => ({
  ...state,
  loading: false,
  authenticated: false,
  avatar: null,
  birth_date: '',
  email: null,
  firstName: null,
  id: null,
  lastName: null,
  name: null,
  nickname: null,
  phone: null,
  promotionsOptOut: false,
});

export const customerReducer: Reducer<CustomerReducerState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case HANDLE_INVALID_LOGIN_TOKEN:
      return state.id ? { ...state, authenticated: false } : state;
    case CUSTOMER_LOG_IN:
    case CUSTOMER_WHOAMI:
      return { ...state, loading: true };
    case CUSTOMER_LOG_IN_ERROR:
    case CUSTOMER_REGISTER_ERROR:
      return { ...state, loading: false, logInErrorMessage: action.payload };
    case SET_LOCAL_USER:
      return {
        ...state,
        phone: action.payload.phone || null,
        email: action.payload.email || null,
        firstName: action.payload.first_name || null,
        lastName: action.payload.last_name || null,
        birth_date: action.payload.birth_date || '',
        externalId: action.payload.externalId || null,
        promotionsOptOut: action.payload.promotions_opt_out,
      };
    case HEADLESS_CUSTOMER_LOG_IN_SUCCESS:
    case CUSTOMER_LOG_IN_SUCCESS:
    case CUSTOMER_REGISTER_SUCCESS:
    case CUSTOMER_SET_SIGNED_IN:
      return setUser(
        { ...state, loading: false, logInErrorMessage: null },
        action.payload
      );
    case UPDATE_IDENTIFICATION_SUCCESS:
      return setUserFromPartialIdentification(state, action.payload);
    case CUSTOMER_LOG_OUT:
    case CUSTOMER_WHOAMI_ERROR:
      return reset(state);
    case CUSTOMER_WHOAMI_SUCCESS:
      return setUser(
        { ...state, loading: false, sessionChecked: true },
        action.payload
      );
    case UPDATE_USERS:
      return setUser(state, action.payload);
    case VERIFY_STORE_SUCCESS:
      return { ...state, isEmbeddedMode: true };
  }
  return state;
};

export const selectCustomer = (state: AppState) => state.customer;
